import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { disconnectSocket } from "../socket";
import { logout } from "./funtionsBD/Authservice";
import { useBrand } from "../context/BrandContext";
import { clearLocalStoragePreservingBrand } from "../utils/localStorageBrand";
import { userHasParentRole } from "../utils/homeRole";
import { InstallAppButton } from "./InstallAppButton";
import "./UserAction.css";

// SVG Icons matching premium iOS UI
const ChevronRight = () => (
  <svg className="user-action-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const KeyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const AwardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="7"></circle>
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
  </svg>
);

const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

export const UserActions = ({ user }) => {
  const [token] = useState(localStorage.getItem("token"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { themeMode, setThemeMode } = useBrand();

  const handleLogout = async () => {
    try {
      await logout(token);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearLocalStoragePreservingBrand();
      sessionStorage.clear();
      disconnectSocket();
      navigate("/login");
    }
  };

  const handleProfileNavigation = () => {
    const profilePath =
      user.isSuperAdmin ? "/SuperAdminProfile" : "/EditProfile";
    navigate(profilePath);
  };

  const isCoach = user.type_user_id === 3;
  const isParent = userHasParentRole(user);

  return (
    <div className="user-actions">
      {/* Group 1: General Settings */}
      <div className="user-action-buttons">
        {/* Profile row */}
        <button className="user-action-button-link" onClick={handleProfileNavigation}>
          <div className="user-action-left">
            <div className="ios-setting-icon-badge profile">
              <UserIcon />
            </div>
            <p>{t("myprofile")}</p>
          </div>
          <ChevronRight />
        </button>

        {isParent && (
          <>
            <hr />
            <button
              className="user-action-button-link"
              onClick={() => navigate("/parent/progress")}
            >
              <div className="user-action-left">
                <div className="ios-setting-icon-badge level">
                  <AwardIcon />
                </div>
                <p>{t("parentOpenProgressHub") || "Progress Hub"}</p>
              </div>
              <ChevronRight />
            </button>
          </>
        )}

        <hr />

        {/* Password row */}
        <button className="user-action-button-link" onClick={() => navigate("/ChangePassword")}>
          <div className="user-action-left">
            <div className="ios-setting-icon-badge password">
              <KeyIcon />
            </div>
            <p>{t("changepassword")}</p>
          </div>
          <ChevronRight />
        </button>

        {/* Language row (only if not super admin) */}
        {!user.isSuperAdmin && (
          <>
            <hr />
            <button className="user-action-button-link" onClick={() => navigate("/ChangeLanguage")}>
              <div className="user-action-left">
                <div className="ios-setting-icon-badge language">
                  <GlobeIcon />
                </div>
                <p>{t("changelanguage")}</p>
              </div>
              <ChevronRight />
            </button>
          </>
        )}

        {/* Theme Selection */}
        <hr />
        <div className="user-action-button-link theme-toggle-row">
          <div className="user-action-left">
            <div className="ios-setting-icon-badge theme">
              {themeMode === "light" ? <SunIcon /> : <MoonIcon />}
            </div>
            <p>{t("themeMode")}</p>
          </div>
          <div className="theme-toggle-options">
            <button
              className={`theme-toggle-btn ${themeMode === "light" ? "active" : ""}`}
              onClick={() => setThemeMode("light")}
            >
              {t("lightMode")}
            </button>
            <button
              className={`theme-toggle-btn ${themeMode === "dark" ? "active" : ""}`}
              onClick={() => setThemeMode("dark")}
            >
              {t("darkMode")}
            </button>
          </div>
        </div>

        <hr />
        <InstallAppButton variant="row" hideWhenInstalled={false} />

        {/* Coach Competency Block row */}
        {isCoach && (
          <>
            <hr />
            <button className="user-action-button-link" onClick={() => navigate("/YourCBLevel")}>
              <div className="user-action-left">
                <div className="ios-setting-icon-badge level">
                  <AwardIcon />
                </div>
                <p>{t("competencyblock")}</p>
              </div>
              <ChevronRight />
            </button>
          </>
        )}
      </div>

      {/* Group 2: Standalone Destructive Logout Settings Card */}
      <div className="user-action-buttons">
        <button className="user-action-button-link destructive" onClick={handleLogout}>
          <div className="user-action-left">
            <div className="ios-setting-icon-badge logout">
              <LogoutIcon />
            </div>
            <p>{t("logout")}</p>
          </div>
          <ChevronRight />
        </button>
      </div>
    </div>
  );
};
